import React, { useState, useEffect } from 'react';
import { useFormStatus } from '../hooks/useFormStatus';
import { createTeam } from '../utils/teams';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';
import { BiEdit } from 'react-icons/bi';

const Team = () => {
  const { isOpen, loading, status } = useFormStatus('team');
  const [formData, setFormData] = useState({
    team_name: '',
    member_emails: [''],
    idea: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  
  // Track member validation status
  const [memberStatus, setMemberStatus] = useState([{ 
    email: '', 
    name: null, 
    isValid: false, 
    isEditing: true,
    error: null,
    loading: false 
  }]);
  const [validationErrors, setValidationErrors] = useState({});
  const [debounceTimers, setDebounceTimers] = useState({});

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [debounceTimers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate member email against database
  const validateMemberEmail = async (email, index) => {
    if (!email || !email.trim()) {
      return;
    }

    // Blur the input field to show loading state
    document.activeElement?.blur();

    // Set loading state
    const loadingStatus = [...memberStatus];
    loadingStatus[index] = {
      ...loadingStatus[index],
      loading: true
    };
    setMemberStatus(loadingStatus);

    try {
      const { data, error } = await supabase
        .from('applicants')
        .select('full_name, status, email')
        .eq('email', email.trim())
        .single();

      const newMemberStatus = [...memberStatus];

      if (error || !data) {
        // Email not registered
        newMemberStatus[index] = {
          email: email,
          name: null,
          isValid: false,
          isEditing: true,
          error: 'هذا البريد الإلكتروني غير مسجل',
          loading: false
        };
        
        // Show error and hide after 5 seconds
        setValidationErrors(prev => ({ ...prev, [index]: 'هذا البريد الإلكتروني غير مسجل' }));
        setTimeout(() => {
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[index];
            return newErrors;
          });
        }, 5000);
      } else if (data.status !== 'مقبول') {
        // Email registered but not accepted
        newMemberStatus[index] = {
          email: email,
          name: data.full_name,
          isValid: false,
          isEditing: true,
          error: 'هذا المتقدم لم يتم قبوله بعد',
          loading: false
        };
        
        // Show error and hide after 5 seconds
        setValidationErrors(prev => ({ ...prev, [index]: 'هذا المتقدم لم يتم قبوله بعد' }));
        setTimeout(() => {
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[index];
            return newErrors;
          });
        }, 5000);
      } else {
        // Email registered and accepted
        newMemberStatus[index] = {
          email: email,
          name: data.full_name,
          isValid: true,
          isEditing: false,
          error: null,
          loading: false
        };
        
        // Clear any existing errors
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
      }

      setMemberStatus(newMemberStatus);
    } catch (err) {
      console.error('Error validating email:', err);
      // Clear loading state on error
      const errorStatus = [...memberStatus];
      errorStatus[index] = {
        ...errorStatus[index],
        loading: false
      };
      setMemberStatus(errorStatus);
    }
  };

  const handleMemberEmailChange = (index, value) => {
    const newMemberEmails = [...formData.member_emails];
    newMemberEmails[index] = value;
    setFormData(prev => ({
      ...prev,
      member_emails: newMemberEmails
    }));

    // Update member status to editing mode
    const newMemberStatus = [...memberStatus];
    newMemberStatus[index] = {
      ...newMemberStatus[index],
      email: value,
      isEditing: true,
      isValid: false
    };
    setMemberStatus(newMemberStatus);

    // Clear any existing timer for this index
    if (debounceTimers[index]) {
      clearTimeout(debounceTimers[index]);
    }

    // Email regex pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // If value matches email format, set a timer to auto-validate
    if (value && emailRegex.test(value.trim())) {
      const timer = setTimeout(() => {
        // Auto-trigger validation after 1.5 seconds
        validateMemberEmail(value, index);
      }, 1500);

      setDebounceTimers(prev => ({
        ...prev,
        [index]: timer
      }));
    }
  };

  const handleEmailBlur = (index) => {
    // Clear any pending timer since user manually blurred
    if (debounceTimers[index]) {
      clearTimeout(debounceTimers[index]);
      setDebounceTimers(prev => {
        const newTimers = { ...prev };
        delete newTimers[index];
        return newTimers;
      });
    }
    
    const email = formData.member_emails[index];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    // Only validate if it's a valid email format
    if (email && email.trim() && emailRegex.test(email.trim())) {
      validateMemberEmail(email, index);
    }
  };

  const startEditingMember = (index) => {
    const newMemberStatus = [...memberStatus];
    newMemberStatus[index] = {
      ...newMemberStatus[index],
      isEditing: true
    };
    setMemberStatus(newMemberStatus);
  };

  const addMemberField = () => {
    setFormData(prev => ({
      ...prev,
      member_emails: [...prev.member_emails, '']
    }));
    setMemberStatus(prev => [...prev, { 
      email: '', 
      name: null, 
      isValid: false, 
      isEditing: true,
      error: null,
      loading: false 
    }]);
  };

  const removeMemberField = (index) => {
    if (formData.member_emails.length > 1) {
      // Clear any pending timer for this field
      if (debounceTimers[index]) {
        clearTimeout(debounceTimers[index]);
      }
      
      const newMemberEmails = formData.member_emails.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        member_emails: newMemberEmails
      }));
      setMemberStatus(prev => prev.filter((_, i) => i !== index));
      
      // Clear validation error for this index
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      
      // Clear and re-index timers
      setDebounceTimers(prev => {
        const newTimers = {};
        Object.keys(prev).forEach(key => {
          const idx = parseInt(key);
          if (idx < index) {
            newTimers[idx] = prev[idx];
          } else if (idx > index) {
            newTimers[idx - 1] = prev[idx];
          }
        });
        return newTimers;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    // Filter out empty member emails
    const filteredMemberEmails = formData.member_emails.filter(email => email.trim() !== '');
    
    const result = await createTeam({
      ...formData,
      member_emails: filteredMemberEmails
    });
    
    setMessage(result.message);
    setSubmitting(false);

    if (result.success) {
      setSubmitted(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05339c]">
        <PublicNav />
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-lg text-white">جاري التحميل...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isOpen) {
    return (
      <div className="min-h-screen bg-[#05339c]">
        <PublicNav />
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center bg-dark-blue p-8 rounded-2xl shadow-2xl max-w-md mx-4 border-2 border-yellow">
            <h1 className="text-2xl font-semibold text-yellow mb-4">فترة تسجيل الفرق مغلقة</h1>
            <p className="text-white">حالة النموذج: {status}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Success page after submission
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#05339c]">
        <PublicNav />
        <div className="flex justify-center items-center min-h-[80vh] p-6">
          <div className="bg-dark-blue rounded-2xl shadow-2xl p-12 border-2 border-green-500 max-w-md w-full text-center">
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center border-4 border-green-500">
                <svg className="w-12 h-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-green-400 mb-4">تم التسجيل بنجاح!</h2>
            <p className="text-white text-lg ">{message}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05339c]">
      <PublicNav />
      <div className="max-w-2xl mx-auto p-6 py-32">
        <div className="bg-dark-blue rounded-2xl shadow-2xl p-8 border-2 border-yellow">
          <h1 className="text-3xl font-semibold text-center mb-8 text-white">
            تسجيل <span className="text-yellow">فريق</span>
          </h1>
        
        {message && !submitted && (
          <div className="mb-6 p-4 rounded-lg flex items-start gap-3 bg-red-500/20 border-2 border-red-500 text-red-400">
            <span className="flex-1">{message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-yellow mb-2">
            اسم الفريق *
          </label>
          <input
dir="ltr"            type="text"
            name="team_name"
            value={formData.team_name}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-yellow mb-2">
            ايميل العضو *
          </label>
          {formData.member_emails.map((email, index) => (
            <div key={index} className="mb-4">
              <div className="flex gap-2 items-center">
                {memberStatus[index]?.loading ? (
                  // Loading state: Show spinner
                  <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-blue/20 border border-yellow rounded-md">
                    <span className="text-white flex-1">جاري التحقق...</span>
                    <svg className="animate-spin h-5 w-5 text-yellow" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : memberStatus[index]?.isValid && !memberStatus[index]?.isEditing ? (
                  // Display mode: Show name with check mark and edit icon
                  <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-blue/20 border border-yellow rounded-md">
                    <span className="text-white font-medium flex-1">
                      {memberStatus[index].name}
                    </span>
                    <button
                      type="button"
                      onClick={() => startEditingMember(index)}
                      className="text-yellow hover:text-yellow/80 transition-colors"
                      title="تعديل"
                    >
                      <BiEdit className="w-5 h-5 text-yellow" />
                    </button>
                  </div>
                ) : (
                  // Edit mode: Show input field
                  <input
dir="ltr"                    type="email"
                    value={email}
                    onChange={(e) => handleMemberEmailChange(index, e.target.value)}
                    onBlur={() => handleEmailBlur(index)}
                    required={index === 0}
                    disabled={memberStatus[index]?.loading}
                    className="flex-1 px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="البريد الإلكتروني للعضو"
                  />
                )}
                
                {formData.member_emails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeMemberField(index)}
                    className="p-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                    title="حذف"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Validation error message */}
              {validationErrors[index] && (
                <div className="mt-2 text-red-400 text-sm">
                  {validationErrors[index]}
                </div>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addMemberField}
            className="mt-2 px-4 py-2 bg-yellow text-dark-blue rounded-md hover:bg-yellow/80 transition-all duration-300 font-semibold flex items-center gap-2"
          >
            <span className="text-lg">+</span>
            <span>إضافة عضو</span>
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-yellow mb-2">
            فكرة المشروع *
          </label>
          <textarea
            name="idea"
            value={formData.idea}
            onChange={handleInputChange}
            required
            rows="4"
            className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
            placeholder="اشرح فكرة مشروعك..."
          />
        </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-yellow text-dark-blue py-3 px-4 rounded-md hover:bg-yellow/80 focus:outline-none focus:ring-2 focus:ring-yellow disabled:opacity-50 transition-all duration-300 font-bold shadow-lg"
          >
            {submitting ? 'جاري الإرسال...' : 'تسجيل الفريق'}
          </button>
        </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Team;
